import * as api from "@/script/api"
import { default as MapData } from "@/assets/data/map_data.json"
import { default as MarkerTypes } from "@/assets/data/marker_types.json"

import storage from "@/script/storage"

export function getColorByType(typeName, rarity) {
	const colorOverride = storage.state.pref.colorOverrides[typeName]

	if (colorOverride !== undefined && colorOverride[0] === true) {
		return colorOverride[1]
	}

	const colorDefinition = MarkerTypes[typeName]?.color

	if (colorDefinition === undefined)
		throw new Error(`Marker Type '${typeName}' does not exist`)

	if (colorDefinition === null) return "#fff"
	else if (colorDefinition.startsWith("#")) return colorDefinition

	switch (rarity) {
		case MarkedLocation.RARITY.Uncommon:
			return "var(--tcf-uncommon)"
		case MarkedLocation.RARITY.Rare:
			return "var(--tcf-rare)"
		case MarkedLocation.RARITY.Epic:
			return "var(--tcf-epic)"
		case MarkedLocation.RARITY.Exotic:
			return "var(--tcf-exotic)"
		case MarkedLocation.RARITY.Legendary:
			return "var(--tcf-legendary)"
		default:
			return "var(--tcf-common)"
	}
}

export class MarkedLocation {
	static RARITY = {
		Common: 0,
		Uncommon: 1,
		Rare: 2,
		Epic: 3,
		Exotic: 4,
		Legendary: 5,
	}

	constructor(
		uid,
		typeName,
		pos,
		underground,
		behindLock,
		label,
		rarity,
		isLocal
	) {
		this.uid = uid
		this.typeName = typeName
		this.pos = pos
		this.underground = underground
		this.behindLock = behindLock
		this.label = label
		this.rarity = rarity
		this.isLocal = isLocal
	}

	static unmarshal(entry) {
		return new MarkedLocation(
			entry["UID"],
			entry["Type"]["Name"],
			[entry["X"], entry["Y"]],
			entry["Underground"],
			entry["BehindLock"],
			entry["Label"],
			entry["Rarity"],
			false
		)
	}

	getIconName() {
		return MarkerTypes[this.typeName].iconName
	}

	getColor() {
		return getColorByType(this.typeName, this.rarity)
	}

	getImagePath() {
		const cdnPath = "https://cdn.lunati.cc/tcf/previews/"
		const imageName = this.uid + ".jpg"

		return fetch(cdnPath + imageName)
	}
}

class GameMap {
	constructor(id, key, name, namedLocations) {
		this._id = id
		this.key = key
		this.name = name
		this.namedLocations = namedLocations
		this._markedLocations = null
		this._imagePath = null
	}

	async loadImage(callback) {
		if (this._imagePath === null)
			this._imagePath = require(`@/assets/maps/${this.key}.jpg`)

		callback(this._imagePath)
	}

	async fetchMarkedLocations(callback) {
		if (this._markedLocations === null) {
			const jsonData = await api.getLocationsForMap(this._id)

			if (jsonData !== undefined) {
				this._markedLocations = jsonData
					.map(MarkedLocation.unmarshal)
					.filter((entry) =>
						Object.hasOwn(MarkerTypes, entry.typeName)
					)
			}
		}

		callback(this._markedLocations ?? [])
	}
}

const maps = new Map()

export function initMaps() {
	for (const [key, value] of Object.entries(MapData)) {
		maps.set(
			key,
			new GameMap(
				value.id,
				key,
				value.name,
				value.namedLocations.map((entry) => ({
					pos: entry[0],
					name: entry[1],
				}))
			)
		)
	}
}

export function getMapByKey(key) {
	return maps.get(key)
}
